import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Copy, ArrowSquareOut, MagnifyingGlass } from "@phosphor-icons/react";

export default function PagesList() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pages", { params: q ? { q } : {} });
      setPages(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this page? This cannot be undone.")) return;
    try {
      await api.delete(`/pages/${id}`);
      toast.success("Page deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clone = async (id) => {
    try {
      const { data } = await api.post(`/pages/${id}/clone`);
      toast.success("Page cloned");
      nav(`/admin/pages/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleDisplay = async (p) => {
    const nextStatus = p.status === "published" ? "draft" : "published";
    try {
      await api.put(`/pages/${p.id}`, { ...p, status: nextStatus });
      toast.success(nextStatus === "published" ? "Now visible on website" : "Hidden from website");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-pages-list">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="overline">Content</div>
          <h1 className="font-display" style={{ fontSize: 36, fontWeight: 900, margin: "8px 0 0", letterSpacing: "-0.03em" }}>Information Pages</h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ position: "relative" }}>
            <MagnifyingGlass size={14} style={{ position: "absolute", left: 12, top: 13, color: "var(--text-2)" }} />
            <input className="input" placeholder="Search…" value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="pages-search-input"
              style={{ paddingLeft: 34, width: 240 }} />
          </div>
          <Link to="/admin/pages/new" className="btn btn-primary" data-testid="new-page-btn">
            <Plus size={14} weight="bold" /> New Page
          </Link>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 32 }} className="skel" />
        ) : pages.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }}>
            No pages yet. <Link to="/admin/pages/new" style={{ color: "var(--accent)" }}>Create your first page →</Link>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr><th>Title</th><th>Slug</th><th>Display on website</th><th>Scans</th><th style={{ width: 1 }}>Actions</th></tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} data-testid={`page-row-${p.id}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.title}</div>
                    <div style={{ fontSize: 12, color: "var(--text-2)" }}>{p.short_description?.slice(0, 80)}</div>
                  </td>
                  <td style={{ color: "var(--text-2)", fontSize: 13 }}>/{p.slug}</td>
                  <td>
                    <DisplayToggle page={p} onToggle={() => toggleDisplay(p)} />
                  </td>
                  <td style={{ fontWeight: 700 }}>{p.scans || 0}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Link className="btn btn-ghost btn-sm" to={`/admin/pages/${p.id}`} data-testid={`page-edit-${p.id}`}>
                      <PencilSimple size={12} />
                    </Link>
                    <button className="btn btn-ghost btn-sm" onClick={() => clone(p.id)} data-testid={`page-clone-${p.id}`}>
                      <Copy size={12} />
                    </button>
                    {p.status === "published" && (
                      <a className="btn btn-ghost btn-sm" href={`/p/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`page-view-${p.id}`}>
                        <ArrowSquareOut size={12} />
                      </a>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => del(p.id)} data-testid={`page-delete-${p.id}`}>
                      <Trash size={12} color="var(--danger)" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DisplayToggle({ page, onToggle }) {
  const on = page.status === "published";
  return (
    <button onClick={onToggle} data-testid={`page-display-toggle-${page.id}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "transparent", border: 0, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
      <span style={{
        width: 40, height: 22, borderRadius: 12, background: on ? "#16A34A" : "#D4D4D8",
        position: "relative", transition: "background .2s",
      }}>
        <span style={{
          position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18,
          background: "#fff", borderRadius: "50%", transition: "left .2s",
        }} />
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, color: on ? "#15803d" : "#71717A", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {on ? "Yes" : "No"}
      </span>
    </button>
  );
}
